import {
  clearWikiUser,
  readWikiToken,
  writeWikiSession,
  type WikiUser,
} from './wiki-auth';
import { GAMEKEE_IDENTITY_ALIAS } from './auth-config';
import { LOCAL_AUTH_BYPASS, LOCAL_WIKI_USER } from './local-auth';
import { shareAsync } from './share-async';

type ApiEnvelope<T> = { code: number; msg: string; data: T; meta?: unknown };
type ApiUser = {
  uid: number;
  username: string;
  avatar?: string;
  phone?: string;
  email?: string;
  description?: string;
  status?: number;
  verify_level?: number;
};

const apiBase = '/api/gamekee';

async function requestEnvelope<T>(
  path: string,
  init: RequestInit = {},
  protectedRequest = false,
): Promise<ApiEnvelope<T>> {
  const token = readWikiToken();
  const headers = new Headers(init.headers);
  headers.set('Content-Type', 'application/json');
  headers.set('Game-Alias', GAMEKEE_IDENTITY_ALIAS);
  headers.set('Device-Num', '1');
  if (token) headers.set('Access-Token', token);
  const response = await fetch(`${apiBase}${path}`, { ...init, headers });
  const payload = (await response.json()) as ApiEnvelope<T>;
  if (response.status === 401 || payload.code === 401) {
    if (protectedRequest) clearWikiUser();
    throw new Error(payload.msg || '登录状态已失效，请重新登录。');
  }
  if (!response.ok || payload.code !== 0)
    throw new Error(
      payload.msg || `请求失败（${payload.code || response.status}）`,
    );
  return payload;
}

async function request<T>(
  path: string,
  init: RequestInit = {},
  protectedRequest = false,
): Promise<T> {
  return (await requestEnvelope<T>(path, init, protectedRequest)).data;
}

function toWikiUser(user: ApiUser, method: WikiUser['method']): WikiUser {
  return {
    uid: user.uid,
    name: user.username,
    avatar: user.avatar || '',
    method,
    phone: user.phone,
    email: user.email,
    description: user.description,
  };
}

export async function sendLoginCode(
  method: 'phone' | 'email',
  account: string,
  captchaId = '',
) {
  return request<{ captcha_result: boolean; biz_result: string }>(
    method === 'phone' ? '/public/sendSmsCode' : '/public/sendEmailCode',
    {
      method: 'POST',
      body: JSON.stringify({
        [method]: account,
        scene: 1,
        captcha_id: captchaId,
      }),
    },
  );
}

export async function loginWithCode(
  method: 'phone' | 'email',
  account: string,
  code: string,
) {
  const data = await request<{ token: string; user: ApiUser }>(
    method === 'phone' ? '/user/auth' : '/user/email/auth',
    {
      method: 'POST',
      body: JSON.stringify({
        [method]: account,
        code,
        ...(method === 'phone' ? { login_type: 1 } : {}),
      }),
    },
  );
  const user = toWikiUser(data.user, method);
  writeWikiSession(user, data.token);
  return user;
}

const myInfoInflight = new Map<string, Promise<WikiUser | null>>();

export async function refreshWikiSession() {
  if (LOCAL_AUTH_BYPASS) return { ...LOCAL_WIKI_USER };
  const token = readWikiToken();
  if (!token) return null;
  return shareAsync(myInfoInflight, token, async () => {
    const data = await request<{ token: string; user: ApiUser }>(
      '/protected/user/myInfo',
      { method: 'GET' },
      true,
    );
    const current = readWikiToken();
    const method = data.user.phone
      ? 'phone'
      : data.user.email
        ? 'email'
        : 'nickname';
    const user = toWikiUser(data.user, method);
    writeWikiSession(user, data.token || current || '');
    return user;
  });
}

export type GameKeePagination = {
  page_no: number;
  limit: number;
  page_total: number;
  total: number;
};
export type GameKeeCommentUser = {
  uid: number | string;
  username: string;
  avatar?: string;
};
export type GameKeeComment = {
  id: number;
  content_id?: number;
  pid: number;
  content: string;
  like_count?: number;
  is_pinned?: boolean;
  is_like?: boolean;
  created_at?: string | number;
  user: GameKeeCommentUser;
  to_user?: GameKeeCommentUser;
  child?: GameKeeComment[];
  child_num?: number;
};

export type CommentReaction = 'like' | 'dislike';

const commentsInflight = new Map<
  string,
  Promise<{ items: GameKeeComment[]; pagination: undefined }>
>();
const postLikeInflight = new Map<string, Promise<boolean>>();
const commentReactionsInflight = new Map<
  string,
  Promise<Record<string, CommentReaction>>
>();

export type GameKeePost = {
  id: number;
  title: string;
  summary?: string;
  thumb?: string;
  description?: string;
  content?: string;
  cover?: string;
  content_type?: number;
  view_count?: number;
  comment_count?: number;
  like_count?: number;
  created_at?: string | number;
  updated_at?: string | number;
  user?: GameKeeCommentUser;
  game?: { id?: number; name?: string; alias?: string; icon?: string };
};
export type GameKeeUserComment = {
  id: number;
  content_id?: number;
  game_id?: number;
  pid?: number;
  content: string;
  like_count?: number;
  created_at?: string | number;
  reply_content?: string;
  title?: string;
  game?: { gid?: number; name?: string; alias?: string };
};
export type GameKeeFavorite = {
  game?: { id?: number; name?: string; alias?: string; icon?: string };
  content: GameKeePost;
  user?: GameKeeCommentUser;
};

export function formatGameKeeDate(value?: string | number, withTime = false) {
  if (!value) return '';
  const date = new Date(
    typeof value === 'number' && value < 1e12 ? value * 1000 : value,
  );
  if (Number.isNaN(date.getTime())) return '';
  return withTime
    ? date.toLocaleString('zh-CN')
    : date.toLocaleDateString('zh-CN');
}

function queryString(values: Record<string, string | number | undefined>) {
  const query = new URLSearchParams();
  Object.entries(values).forEach(([key, value]) => {
    if (value !== undefined && value !== '') query.set(key, String(value));
  });
  return query.toString();
}

export async function getComments(params: {
  contentId?: number | string;
  linkUrl?: string;
  type?: 0 | 5 | 28;
  pageNo?: number;
  limit?: number;
}) {
  const target = parseLocalTarget(params.linkUrl, params.contentId);
  const cacheKey = `${target.game}:${target.type}:${target.key}`;
  return shareAsync(commentsInflight, cacheKey, async () => {
    const response = await fetch(
      `/api/games/${encodeURIComponent(target.game)}/comments?${new URLSearchParams({ targetType: target.type, targetKey: target.key })}`,
    );
    const payload = (await response.json()) as {
      comments?: Array<{
        id: string;
        parentId?: string;
        authorId: string;
        author: string;
        avatar?: string;
        content: string;
        createdAt: string;
        likeCount: number;
        isPinned?: boolean;
      }>;
      error?: string;
    };
    if (!response.ok) throw new Error(payload.error || '评论加载失败');
    const rows = payload.comments || [];
    const mapped = new Map(
      rows.map((item) => [
        item.id,
        {
          id: item.id as unknown as number,
          pid: item.parentId ? 1 : 0,
          content: item.content,
          like_count: item.likeCount,
          is_pinned: item.isPinned === true,
          created_at: item.createdAt,
          user: {
            uid: item.authorId,
            username: item.author,
            avatar: item.avatar,
          },
          child: [],
        } as GameKeeComment,
      ]),
    );
    const roots: GameKeeComment[] = [];
    rows.forEach((item) => {
      const current = mapped.get(item.id)!;
      if (item.parentId && mapped.has(item.parentId))
        mapped.get(item.parentId)!.child!.push(current);
      else roots.push(current);
    });
    return { items: roots, pagination: undefined };
  });
}

export async function addComment(params: {
  content: string;
  contentId?: number | string;
  linkUrl?: string;
  type?: 0 | 5 | 28;
  pid?: number;
  uid?: number | string;
  commentId?: number;
}) {
  const target = parseLocalTarget(params.linkUrl, params.contentId),
    token = readWikiToken();
  const response = await fetch(
    `/api/games/${encodeURIComponent(target.game)}/comments`,
    {
      method: 'POST',
      headers: {
        'content-type': 'application/json',
        ...(token ? { 'access-token': token } : {}),
      },
      body: JSON.stringify({
        targetType: target.type,
        targetKey: target.key,
        parentId: params.commentId ? String(params.commentId) : undefined,
        content: params.content,
        targetAuthorId: params.uid ? String(params.uid) : undefined,
      }),
    },
  );
  const payload = (await response.json()) as { id?: string; error?: string };
  if (!response.ok) throw new Error(payload.error || '评论发布失败');
  return { id: payload.id as unknown as number, is_pass: true };
}

export async function getPostLike(game: string, post: string): Promise<boolean> {
  const token = readWikiToken();
  if (!token) return false;
  return shareAsync(postLikeInflight, `${game}:${post}:${token.slice(0, 12)}`, async () => {
    const response = await fetch(`/api/games/${encodeURIComponent(game)}/posts/${encodeURIComponent(post)}/reaction`, {
      headers: { 'access-token': token }, cache: 'no-store',
    });
    const payload = (await response.json()) as { liked?: boolean; error?: string };
    if (!response.ok) throw new Error(payload.error || '帖子点赞状态读取失败');
    return payload.liked === true;
  });
}

export async function setPostLike(game: string, post: string, enabled: boolean, url: string) {
  const token = readWikiToken();
  const response = await fetch(`/api/games/${encodeURIComponent(game)}/interactions`, {
    method: 'POST',
    headers: { 'content-type': 'application/json', ...(token ? { 'access-token': token } : {}) },
    body: JSON.stringify({ action: 'like', enabled, targetType: 'post', targetKey: post, url }),
  });
  const payload = (await response.json()) as { enabled?: boolean; error?: string };
  if (!response.ok) throw new Error(payload.error || '帖子点赞失败');
  return payload;
}

export async function getCommentReactions(linkUrl: string): Promise<Record<string, CommentReaction>> {
  const token = readWikiToken();
  if (!token) return {};
  const target = parseLocalTarget(linkUrl);
  return shareAsync(
    commentReactionsInflight,
    `${target.game}:${target.type}:${target.key}:${token.slice(0, 12)}`,
    async () => {
      const query = new URLSearchParams({ targetType: target.type, targetKey: target.key });
      const response = await fetch(`/api/games/${encodeURIComponent(target.game)}/comments/reactions?${query}`, {
        headers: { 'access-token': token },
      });
      const payload = (await response.json()) as { reactions?: Record<string, CommentReaction>; error?: string };
      if (!response.ok) throw new Error(payload.error || '评论互动读取失败');
      return payload.reactions || {};
    },
  );
}

export async function setCommentReaction(params: {
  linkUrl: string;
  commentId: number | string;
  action: CommentReaction;
  enabled: boolean;
  authorId?: number | string;
}) {
  const target = parseLocalTarget(params.linkUrl);
  const token = readWikiToken();
  const response = await fetch(`/api/games/${encodeURIComponent(target.game)}/interactions`, {
    method: 'POST',
    headers: {
      'content-type': 'application/json',
      ...(token ? { 'access-token': token } : {}),
    },
    body: JSON.stringify({
      action: params.action,
      enabled: params.enabled,
      targetType: 'comment',
      targetKey: String(params.commentId),
      targetAuthorId: params.authorId ? String(params.authorId) : undefined,
      url: params.linkUrl,
    }),
  });
  const payload = (await response.json()) as { enabled?: boolean; error?: string };
  if (!response.ok) throw new Error(payload.error || '评论互动失败');
  return payload;
}

function parseLocalTarget(linkUrl?: string, contentId?: number | string) {
  const path = (linkUrl || '').split('?')[0].split('/').filter(Boolean);
  const game = path[0] || 'arcanatactics';
  const last = String(contentId || path.at(-1) || linkUrl || 'page');
  return {
    game,
    type: last.startsWith('t')
      ? 'hero'
      : path.includes('guide')
        ? 'post'
        : 'page',
    key: last.startsWith('t') ? last.slice(1) : last,
  };
}

export async function getUserPosts(
  uid: number,
  options: { pageNo?: number; limit?: number } = {},
) {
  const query = queryString({
    uid,
    audit_state: 0,
    page_no: options.pageNo ?? 1,
    limit: options.limit ?? 20,
  });
  const payload = await requestEnvelope<GameKeePost[]>(
    `/user/contentList?${query}`,
  );
  return {
    items: payload.data || [],
    pagination: (payload.meta as { pagination?: GameKeePagination } | undefined)
      ?.pagination,
  };
}

export async function getUserComments(
  uid: number,
  options: { pageNo?: number; limit?: number } = {},
) {
  const query = queryString({
    uid,
    page_no: options.pageNo ?? 1,
    limit: options.limit ?? 20,
  });
  const payload = await requestEnvelope<GameKeeUserComment[]>(
    `/user/commentList?${query}`,
  );
  return {
    items: payload.data || [],
    pagination: (payload.meta as { pagination?: GameKeePagination } | undefined)
      ?.pagination,
  };
}

export async function getUserFavorites(
  uid: number,
  options: { pageNo?: number; limit?: number } = {},
) {
  const query = queryString({
    uid,
    type: 1,
    game_id: 0,
    page_no: options.pageNo ?? 1,
    limit: options.limit ?? 20,
  });
  const payload = await requestEnvelope<GameKeeFavorite[]>(
    `/user/favoriteList?${query}`,
  );
  return {
    items: payload.data || [],
    pagination: (payload.meta as { pagination?: GameKeePagination } | undefined)
      ?.pagination,
  };
}
